import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    PlayCircle,
    Activity as ActivityIcon,
    BarChart3,
    Database,
    Zap,
    LayoutGrid,
    Users,
    Rocket,
    Send,
    LifeBuoy,
    Settings as SettingsIcon,
    ChevronDown,
    LogOut,
    Sparkles,
    GraduationCap,
    Headset,
    Check,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ORCHID_LOGO } from "@/lib/brand";
import { adminApi, getActiveAgent, setActiveAgent } from "@/lib/api";
import LiveChatNotifier from "@/components/LiveChatNotifier";

const NAV = [
    { type: "link", to: "/playground", label: "Playground", icon: PlayCircle, testid: "nav-playground" },
    { type: "link", to: "/train", label: "Train Agent", icon: GraduationCap, testid: "nav-train" },
    { type: "link", to: "/live-chats", label: "Live Chats", icon: Headset, testid: "nav-live-chats" },
    {
        type: "group", key: "activity", label: "Activity", icon: ActivityIcon, testid: "nav-activity",
        children: [
            { to: "/activity/chats", label: "Chat logs", testid: "nav-activity-chats" },
            { to: "/activity/leads", label: "Leads", testid: "nav-activity-leads" },
            { to: "/activity/tickets", label: "Support tickets", testid: "nav-activity-tickets" },
        ],
    },
    {
        type: "group", key: "analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics", ownerOnly: true,
        children: [
            { to: "/analytics/chats", label: "Chats", testid: "nav-analytics-chats" },
            { to: "/analytics/topics", label: "Topics", testid: "nav-analytics-topics" },
            { to: "/analytics/sentiment", label: "Sentiment", testid: "nav-analytics-sentiment" },
        ],
    },
    {
        type: "group", key: "data-sources", label: "Data sources", icon: Database, testid: "nav-data-sources",
        children: [
            { to: "/data-sources/files", label: "Files", testid: "nav-files" },
            { to: "/data-sources/snippets", label: "Text snippets", testid: "nav-snippets" },
            { to: "/data-sources/website", label: "Website", testid: "nav-website" },
            { to: "/data-sources/qa", label: "Q\u0026A", testid: "nav-qa" },
            { to: "/data-sources/notion", label: "Notion", testid: "nav-notion" },
            { to: "/data-sources/tickets", label: "Tickets", testid: "nav-data-tickets" },
            { to: "/data-sources/suggestions", label: "Suggestions", testid: "nav-suggestions" },
        ],
    },
    { type: "link", to: "/actions", label: "Actions", icon: Zap, testid: "nav-actions", ownerOnly: true },
    { type: "link", to: "/widgets", label: "Widgets", icon: LayoutGrid, testid: "nav-widgets", ownerOnly: true },
    { type: "link", to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts", ownerOnly: true },
    { type: "link", to: "/deploy", label: "Deploy", icon: Rocket, testid: "nav-deploy" },
    { type: "link", to: "/outbound", label: "Outbound", icon: Send, badge: "Beta", testid: "nav-outbound", ownerOnly: true },
    {
        type: "group", key: "helpdesk", label: "Help desk", icon: LifeBuoy, badge: "Beta", testid: "nav-helpdesk", ownerOnly: true,
        children: [
            { to: "/helpdesk/email", label: "Email", testid: "nav-helpdesk-email" },
            { to: "/helpdesk/assignment", label: "Ticket assignment", testid: "nav-helpdesk-assignment" },
            { to: "/helpdesk/scheduling", label: "Scheduling", testid: "nav-helpdesk-scheduling" },
            { to: "/helpdesk/translation", label: "Translation", testid: "nav-helpdesk-translation" },
        ],
    },
    {
        type: "group", key: "settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", ownerOnly: true,
        children: [
            { to: "/settings/users", label: "Users", testid: "nav-settings-users" },
            { to: "/settings/general", label: "General", testid: "nav-settings-general" },
            { to: "/settings/ai", label: "AI", testid: "nav-settings-ai" },
            { to: "/settings/voice", label: "Voice", testid: "nav-settings-voice" },
            { to: "/settings/integrations", label: "Integrations", testid: "nav-settings-integrations" },
            { to: "/settings/emails", label: "Emails", testid: "nav-settings-emails" },
            { to: "/settings/chat-interface", label: "Chat Interface", testid: "nav-settings-chat-interface" },
            { to: "/settings/security", label: "Security", testid: "nav-settings-security" },
            { to: "/settings/custom-domains", label: "Custom domains", testid: "nav-settings-custom-domains" },
            { to: "/settings/webhooks", label: "Webhooks", testid: "nav-settings-webhooks" },
            { to: "/settings/notifications", label: "Notifications", testid: "nav-settings-notifications" },
            { to: "/settings/lead-email", label: "Lead Emails (SMTP)", testid: "nav-settings-lead-email" },
            { to: "/settings/agents", label: "Live Agents", testid: "nav-settings-agents" },
            { to: "/settings/canned", label: "Canned Replies", testid: "nav-settings-canned" },
            { to: "/settings/fundle", label: "Fundle Loyalty", testid: "nav-settings-fundle" },
        ],
    },
];

const AGENT_FALLBACK = {
    priya: { id: "priya", name: "Priya", kind: "concierge", avatar_url: null, accent: "#7E2A6C" },
    tara: { id: "tara", name: "Tara", kind: "hr", avatar_url: null, accent: "#0F766E" },
};

const AgentSwitcher = () => {
    const [agents, setAgents] = useState([]);
    const [open, setOpen] = useState(false);
    const active = getActiveAgent();

    useEffect(() => {
        adminApi
            .get("/api/admin/bot-agents")
            .then((r) => setAgents(r.data.items || []))
            .catch(() => {});
    }, []);

    const activeAgent =
        agents.find((a) => a.id === active) || AGENT_FALLBACK[active] || AGENT_FALLBACK.priya;
    const options = agents.length ? agents : Object.values(AGENT_FALLBACK);
    const singleAgent = agents.length === 1;

    const select = (id) => {
        if (id === active) {
            setOpen(false);
            return;
        }
        setActiveAgent(id);
        window.location.reload();
    };

    return (
        <div className="px-3 pt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mb-1 px-1">
                Active Agent
            </div>
            <div className="relative" data-testid="agent-switcher">
                <button
                    type="button"
                    data-testid="agent-switcher-btn"
                    onClick={() => !singleAgent && setOpen((o) => !o)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl border border-stone-200 bg-white transition-colors ${singleAgent ? "cursor-default" : "hover:border-orchid-300"}`}
                    style={{ borderLeft: `3px solid ${activeAgent.accent || "#7E2A6C"}` }}
                >
                    {activeAgent.avatar_url ? (
                        <img
                            src={activeAgent.avatar_url}
                            className="h-9 w-9 rounded-full object-cover"
                            alt={activeAgent.name}
                        />
                    ) : (
                        <span
                            className="h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                            style={{ background: activeAgent.accent || "#7E2A6C" }}
                        >
                            {(activeAgent.name || "?")[0]}
                        </span>
                    )}
                    <span className="flex-1 text-left min-w-0">
                        <span className="block text-sm font-semibold text-stone-900 truncate">
                            {activeAgent.name}
                        </span>
                        <span className="block text-[11px] text-stone-500 capitalize truncate">
                            {activeAgent.kind === "hr" ? "HR & Employee" : "Guest Concierge"}
                        </span>
                    </span>
                    <ChevronDown
                        size={15}
                        className={`text-stone-400 transition-transform ${open ? "rotate-180" : ""} ${singleAgent ? "opacity-0" : ""}`}
                    />
                </button>
                {open && !singleAgent && (
                    <div className="absolute z-30 mt-1.5 w-full rounded-xl border border-stone-200 bg-white shadow-xl overflow-hidden">
                        {options.map((a) => (
                            <button
                                key={a.id}
                                type="button"
                                data-testid={`agent-option-${a.id}`}
                                onClick={() => select(a.id)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-stone-50 transition-colors ${
                                    a.id === active ? "bg-orchid-700/5" : ""
                                }`}
                            >
                                {a.avatar_url ? (
                                    <img
                                        src={a.avatar_url}
                                        className="h-8 w-8 rounded-full object-cover"
                                        alt={a.name}
                                    />
                                ) : (
                                    <span
                                        className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                                        style={{ background: a.accent || "#7E2A6C" }}
                                    >
                                        {(a.name || "?")[0]}
                                    </span>
                                )}
                                <span className="flex-1 min-w-0">
                                    <span className="block text-sm font-medium text-stone-900 truncate">
                                        {a.name}
                                    </span>
                                    <span className="block text-[11px] text-stone-500 capitalize truncate">
                                        {a.kind === "hr" ? "HR & Employee" : "Guest Concierge"}
                                    </span>
                                </span>
                                {a.id === active && <Check size={15} className="text-orchid-700" />}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const GroupItem = ({ group, openMap, setOpenMap, currentPath }) => {
    const Icon = group.icon;
    const childActive = group.children.some((c) => currentPath.startsWith(c.to));
    const isOpen = openMap[group.key] ?? childActive;

    return (
        <div>
            <button
                type="button"
                data-testid={group.testid}
                onClick={() => setOpenMap((m) => ({ ...m, [group.key]: !isOpen }))}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    childActive ? "text-orchid-700" : "text-stone-700 hover:bg-stone-100"
                }`}
            >
                <span className="flex items-center gap-3">
                    <Icon size={16} />
                    {group.label}
                    {group.badge && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gold-500/15 text-gold-700">
                            {group.badge}
                        </span>
                    )}
                </span>
                <ChevronDown
                    size={14}
                    className={`transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`}
                />
            </button>
            {isOpen && (
                <div className="mt-0.5 ml-7 space-y-0.5">
                    {group.children.map((child) => (
                        <NavLink
                            key={child.to}
                            to={child.to}
                            data-testid={child.testid}
                            className={({ isActive }) =>
                                `block px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                                    isActive
                                        ? "bg-orchid-700/10 text-orchid-700 font-medium"
                                        : "text-stone-600 hover:bg-stone-100"
                                }`
                            }
                        >
                            {child.label}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
};

export default function AdminLayout() {
    const { admin, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [openMap, setOpenMap] = useState({});

    return (
        <div className="min-h-screen bg-paper flex">
            <LiveChatNotifier />
            <aside className="w-72 bg-white border-r border-stone-200 flex flex-col">
                <div className="px-5 py-5 border-b border-stone-200">
                    <button
                        data-testid="brand-home"
                        onClick={() => navigate("/playground")}
                        className="flex items-center gap-3 group w-full text-left"
                    >
                        <img src={ORCHID_LOGO} alt="The Orchid Hotels" className="h-10 w-auto" />
                        <div>
                            <div className="font-heading text-orchid-700 text-base leading-tight">
                                Orchid Chat Pro
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-600">
                                Multi-Agent Console
                            </div>
                        </div>
                    </button>
                </div>

                <AgentSwitcher />

                <NavLink
                    to="/dashboard"
                    data-testid="nav-dashboard"
                    className={({ isActive }) =>
                        `mx-3 mt-3 flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            isActive ? "bg-orchid-700 text-white" : "text-orchid-700 hover:bg-orchid-700/10"
                        }`
                    }
                >
                    <Sparkles size={16} /> Overview
                </NavLink>

                <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto scroll-soft">
                    {NAV.filter((item) => admin?.role === "owner" || !item.ownerOnly).map((item) => {
                        if (item.type === "link") {
                            const Icon = item.icon;
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={item.testid}
                                    className={({ isActive }) =>
                                        `flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                            isActive
                                                ? "bg-orchid-700/10 text-orchid-700"
                                                : "text-stone-700 hover:bg-stone-100"
                                        }`
                                    }
                                >
                                    <span className="flex items-center gap-3">
                                        <Icon size={16} />
                                        {item.label}
                                    </span>
                                    {item.badge && (
                                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gold-500/15 text-gold-700">
                                            {item.badge}
                                        </span>
                                    )}
                                </NavLink>
                            );
                        }
                        return (
                            <GroupItem
                                key={item.key}
                                group={item}
                                openMap={openMap}
                                setOpenMap={setOpenMap}
                                currentPath={location.pathname}
                            />
                        );
                    })}
                </nav>

                <div className="p-3 border-t border-stone-200">
                    <div className="px-3 py-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-0.5">
                            Signed in
                        </div>
                        <div className="text-xs font-medium text-stone-900 truncate" data-testid="admin-email">
                            {admin?.email}
                        </div>
                    </div>
                    <button
                        data-testid="logout-btn"
                        onClick={logout}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-stone-700 hover:bg-stone-100 transition-colors"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </aside>

            <main className="flex-1 min-w-0 overflow-y-auto scroll-soft">
                <Outlet />
            </main>
        </div>
    );
}
